// packages/shared/src/constants/scheduler.constants.ts
// Single source of truth for scheduler domain value-sets (ADR-20, section 5).

// ── jobLogStatus ────────────────────────────────────────────────────────────
// Status of a job log entry (persisted result of a job run).
export const JOB_LOG_STATUS = ["running", "completed", "failed"] as const;
export type TJobLogStatus = (typeof JOB_LOG_STATUS)[number];
export const JOB_LOG_STATUS_OBJ = {
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, TJobLogStatus>;
export const JOB_LOG_STATUS_LABELS: Record<
  TJobLogStatus,
  Record<string, string>
> = {
  running: { "en-US": "Running", "ru-RU": "Выполняется" },
  completed: { "en-US": "Completed", "ru-RU": "Завершено" },
  failed: { "en-US": "Failed", "ru-RU": "Ошибка" },
};

// ── jobStatus ───────────────────────────────────────────────────────────────
// Runtime status of a scheduled job (includes "idle" — not yet started).
export const JOB_STATUS = ["idle", "running", "completed", "failed"] as const;
export type TJobStatus = (typeof JOB_STATUS)[number];
export const JOB_STATUS_OBJ = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
} as const satisfies Record<string, TJobStatus>;
export const JOB_STATUS_LABELS: Record<TJobStatus, Record<string, string>> = {
  idle: { "en-US": "Idle", "ru-RU": "Ожидание" },
  running: { "en-US": "Running", "ru-RU": "Выполняется" },
  completed: { "en-US": "Completed", "ru-RU": "Завершено" },
  failed: { "en-US": "Failed", "ru-RU": "Ошибка" },
};

export const ASYNC_OPERATION_STATUS_OBJ = {
  STARTED: "started",
} as const;

// ── jobParamType ────────────────────────────────────────────────────────────
// Type of a job parameter field (for UI rendering of param forms).
export const JOB_PARAM_TYPE = [
  "string",
  "date",
  "enum",
  "boolean",
  "number",
  "multiselect",
] as const;
export type TJobParamType = (typeof JOB_PARAM_TYPE)[number];
export const JOB_PARAM_TYPE_OBJ = {
  STRING: "string",
  DATE: "date",
  ENUM: "enum",
  BOOLEAN: "boolean",
  NUMBER: "number",
  MULTISELECT: "multiselect",
} as const satisfies Record<string, TJobParamType>;

// ── notificationChannel ─────────────────────────────────────────────────────
// Delivery channel for scheduler failure notifications (ADR-20, section 5).
export const NOTIFICATION_CHANNEL = [
  "none",
  "webhook",
  "telegram",
  "email",
] as const;
export type TNotificationChannel = (typeof NOTIFICATION_CHANNEL)[number];
export const NOTIFICATION_CHANNEL_OBJ = {
  NONE: "none",
  WEBHOOK: "webhook",
  TELEGRAM: "telegram",
  EMAIL: "email",
} as const satisfies Record<string, TNotificationChannel>;
export const NOTIFICATION_CHANNEL_LABELS: Record<
  TNotificationChannel,
  Record<string, string>
> = {
  none: { "en-US": "Disabled", "ru-RU": "Выключено" },
  webhook: { "en-US": "Webhook", "ru-RU": "Вебхук" },
  telegram: { "en-US": "Telegram", "ru-RU": "Telegram" },
  email: { "en-US": "Email", "ru-RU": "Email" },
};

// Placeholder returned by the API instead of real secrets. A PUT that
// submits this value (or an empty string) must keep the stored secret.
export const NOTIFICATION_SECRET_MASK = "****";
